import { useRef, useState } from "react";
import * as XLSX from "xlsx";

// HOOKS
import useBulkUploadAssets from "../../../hooks/assets/useBulkUpload.asset";
import useBulkEditAssets from "../../../hooks/assets/useBulkEdit.asset";

// COMPONENTS
import ActionBar from "../actionBar.component";
import Button from "../../buttons/default.button";
import Modal from "../../modals/modal.component";

// UTILS
import parseExcelDate from "../../../utils/conversions/dates/excel.date.conversion";

const ASSET_INPUT_FIELDS = new Set([
  "_id", "id", "codi_portal_user_id", "customer_id", "customer_business_name",
  "contact_id", "account", "name", "created_at", "updated_at", "asset_serial",
  "asset_type", "asset_types", "asset_type_id", "make", "model", "service_tag",
  "touch", "warranty", "warranty_type", "warranty_end_date", "warranty_start_date",
]);

const REQUIRED_FIELDS = [
  { key: "customer_id", label: "Customer ID" },
  { key: "asset_serial", label: "Asset Serial" },
  { key: "asset_type", label: "Asset Type" },
  { key: "make", label: "Make" },
  { key: "model", label: "Model" },
  { key: "warranty", label: "Warranty" },
  { key: "warranty_type", label: "Warranty Type" },
];

const validateRows = (rows) => {
  const errors = [];
  rows.forEach((asset, index) => {
    const rowNum = index + 2;
    const ref = asset.asset_serial || `row ${rowNum}`;
    const missing = REQUIRED_FIELDS.filter(({ key }) => {
      const val = asset[key];
      if (key === "warranty") return val === undefined || val === null;
      return val === undefined || val === null || val === "";
    }).map(({ label }) => label);
    if (missing.length > 0) {
      errors.push(`Row ${rowNum} (${ref}): Missing — ${missing.join(", ")}`);
    }
  });
  return errors;
};

// A failed record is either an intentional skip (already exists) or a real
// error — split so the report can show them separately.
const isSkipReason = (reason) => (reason || "").toLowerCase().includes("already exists");

// Fields in AssetInput that are NOT the String scalar — everything else must
// be coerced, since xlsx silently returns numbers for numeric-looking cells
// (e.g. a model/service_tag/account of "3110"), which GraphQL rejects.
const NON_STRING_FIELDS = new Set(["id", "codi_portal_user_id", "asset_types"]);

const toExcelDateString = (value) => {
  if (typeof value === "number") return parseExcelDate(value).toISOString();
  if (value instanceof Date) return value.toISOString();
  return value;
};

const parseExcelRows = (sheet) => {
  const jsonArray = XLSX.utils.sheet_to_json(sheet);
  const rows = [];

  jsonArray.forEach((i) => {
    if (i["WarrantyStartDate"] > i["WarrantyEndDate"]) return;

    const obj = {};
    for (const key in i) {
      const norm = key.replace(/^﻿/, "").toLowerCase().replace(/\s+/g, "_").trim();
      switch (norm) {
        case "asset_type_id":
        case "asset_type":       obj["asset_type"] = String(i[key]); break;
        case "customer_id":      obj["customer_id"] = parseInt(i[key]) || null; break;
        case "contact_id":       obj["contact_id"] = parseInt(i[key]) || null; break;
        case "asset_serial":     obj["asset_serial"] = String(i[key]); break;
        case "warrantystartdate":
        case "warranty_start_date": obj["warranty_start_date"] = toExcelDateString(i[key]); break;
        case "warrantyenddate":
        case "warranty_end_date":   obj["warranty_end_date"] = toExcelDateString(i[key]); break;
        case "warranty":         obj["warranty"] = i[key] === "Yes"; break;
        case "warranty_type":    obj["warranty_type"] = String(i[key]); break;
        case "touch":            obj["touch"] = i[key] === "Yes"; break;
        default:
          obj[norm] = NON_STRING_FIELDS.has(norm) || i[key] == null ? i[key] : String(i[key]);
          break;
      }
    }

    const filtered = {};
    for (const key of Object.keys(obj)) {
      if (ASSET_INPUT_FIELDS.has(key)) filtered[key] = obj[key];
    }
    rows.push(filtered);
  });

  return rows;
};

const AssetActionBar = ({ children }) => {
  const { bulkUploadAssets, loading: isUploading } = useBulkUploadAssets();
  const { bulkEditAssets, loading: isEditing } = useBulkEditAssets();

  const modeRef = useRef(null); // "upload" | "edit"
  const pendingRef = useRef(null); // { mode, rows } — kept around so Retry can resume
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [processingState, setProcessingState] = useState(null); // { mode, total, processed }
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [summaryResult, setSummaryResult] = useState(null); // { mode, succeeded, skipped, errored }
  const [failureState, setFailureState] = useState(null); // { mode, message, partialResult }

  const runBulkOperation = async (mode, rows, resumeFrom) => {
    pendingRef.current = { mode, rows };
    setFailureState(null);
    const alreadyProcessed =
      (resumeFrom?.created?.length ?? resumeFrom?.updated?.length ?? 0) + (resumeFrom?.failed?.length ?? 0);
    setProcessingState({ mode, total: rows.length, processed: alreadyProcessed });

    const onProgress = (processed, total) => {
      setProcessingState({ mode, total, processed });
    };

    try {
      const result = mode === "upload"
        ? await bulkUploadAssets(rows, onProgress, resumeFrom)
        : await bulkEditAssets(rows, onProgress, resumeFrom);

      const succeeded = mode === "upload" ? (result?.created ?? []) : (result?.updated ?? []);
      const allFailed = result?.failed ?? [];
      const skipped = allFailed.filter((item) => isSkipReason(item.reason));
      const errored = allFailed.filter((item) => !isSkipReason(item.reason));
      setSummaryResult({ mode, succeeded, skipped, errored });
      setSummaryModalOpen(true);
      pendingRef.current = null;
    } catch (err) {
      const partialResult = err?.partialResult ?? resumeFrom ?? { created: [], updated: [], failed: [] };
      const processed = (partialResult.created?.length ?? partialResult.updated?.length ?? 0) + (partialResult.failed?.length ?? 0);
      setFailureState({
        mode,
        message: err?.message || "Something went wrong",
        partialResult,
        total: rows.length,
        processed,
      });
    } finally {
      setProcessingState(null);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const mode = modeRef.current;

    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(new Uint8Array(buffer), { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = parseExcelRows(sheet);

    document.getElementById("fileInput").value = "";

    if (!rows.length) {
      alert("No data to upload");
      return;
    }

    const validationErrors = validateRows(rows);
    if (validationErrors.length > 0) {
      setUploadErrors(validationErrors);
      setErrorModalOpen(true);
      return;
    }

    await runBulkOperation(mode, rows);
  };

  const handleRetry = () => {
    if (!pendingRef.current) return;
    const { mode, rows } = pendingRef.current;
    runBulkOperation(mode, rows, failureState?.partialResult);
  };

  const handleCancelRetry = () => {
    pendingRef.current = null;
    setFailureState(null);
  };

  const triggerFile = (mode) => {
    modeRef.current = mode;
    document.getElementById("fileInput").click();
  };

  return (
    <>
      {/* Validation error dialog */}
      <Modal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        containerClassName="bg-white rounded-2xl relative top-20 mx-auto w-full max-w-sm shadow-xl flex flex-col overflow-hidden"
      >
        <div className="px-6 pt-10 pb-4 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">Issues Found</h2>
          <p className="text-sm text-gray-500 mt-1">
            {uploadErrors.length} issue{uploadErrors.length !== 1 ? "s" : ""} found. Fix before retrying.
          </p>
        </div>
        <details className="group flex flex-col min-h-0 px-6 pb-6">
          <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700 select-none list-none flex items-center gap-1">
            <span className="transition-transform group-open:rotate-90">▶</span>
            View details ({uploadErrors.length})
          </summary>
          <ul className="mt-3 flex flex-col gap-2 overflow-y-auto max-h-60 pr-1">
            {uploadErrors.map((err, i) => (
              <li key={i} className="text-sm text-gray-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {err}
              </li>
            ))}
          </ul>
        </details>
      </Modal>

      {/* Summary report dialog */}
      <Modal
        isOpen={summaryModalOpen}
        onClose={() => setSummaryModalOpen(false)}
        containerClassName="bg-white rounded-2xl relative top-20 mx-auto w-full max-w-md shadow-xl flex flex-col overflow-hidden"
      >
        <div className="px-6 pt-8 pb-4 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">
            {summaryResult?.mode === "upload" ? "Upload" : "Edit"} Complete
          </h2>
          <div className="mt-3 flex gap-3">
            <div className="flex-1 bg-green-50 border border-green-200 rounded-xl px-3 py-3 text-center">
              <p className="text-2xl font-bold text-green-700">{summaryResult?.succeeded?.length ?? 0}</p>
              <p className="text-xs text-green-600 mt-0.5">
                {summaryResult?.mode === "upload" ? "Created" : "Updated"}
              </p>
            </div>
            <div className="flex-1 bg-gray-100 border border-gray-200 rounded-xl px-3 py-3 text-center">
              <p className="text-2xl font-bold text-gray-700">{summaryResult?.skipped?.length ?? 0}</p>
              <p className="text-xs text-gray-500 mt-0.5">Skipped</p>
            </div>
            <div className="flex-1 bg-red-50 border border-red-200 rounded-xl px-3 py-3 text-center">
              <p className="text-2xl font-bold text-red-700">{summaryResult?.errored?.length ?? 0}</p>
              <p className="text-xs text-red-600 mt-0.5">Errored</p>
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 flex-1 min-h-0 flex flex-col gap-3 overflow-y-auto">
          {summaryResult?.succeeded?.length > 0 && (
            <details className="group flex flex-col min-h-0">
              <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700 select-none list-none flex items-center gap-1">
                <span className="transition-transform group-open:rotate-90">▶</span>
                {summaryResult?.mode === "upload" ? "Created" : "Updated"} records ({summaryResult.succeeded.length})
              </summary>
              <ul className="mt-3 flex flex-col gap-2 overflow-y-auto max-h-52 pr-1">
                {summaryResult.succeeded.map((item, i) => (
                  <li key={i} className="text-sm text-gray-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    <span className="font-medium">{item.asset_serial}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {summaryResult?.skipped?.length > 0 && (
            <details className="group flex flex-col min-h-0">
              <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700 select-none list-none flex items-center gap-1">
                <span className="transition-transform group-open:rotate-90">▶</span>
                Skipped records ({summaryResult.skipped.length})
              </summary>
              <ul className="mt-3 flex flex-col gap-2 overflow-y-auto max-h-52 pr-1">
                {summaryResult.skipped.map((item, i) => (
                  <li key={i} className="text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <span className="font-medium">{item.asset_serial}</span>
                    <span className="text-gray-500"> — {item.reason}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {summaryResult?.errored?.length > 0 && (
            <details className="group flex flex-col min-h-0" open>
              <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-700 select-none list-none flex items-center gap-1">
                <span className="transition-transform group-open:rotate-90">▶</span>
                Errored records ({summaryResult.errored.length})
              </summary>
              <ul className="mt-3 flex flex-col gap-2 overflow-y-auto max-h-52 pr-1">
                {summaryResult.errored.map((item, i) => (
                  <li key={i} className="text-sm text-gray-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <span className="font-medium">{item.asset_serial}</span>
                    <span className="text-gray-500"> — {item.reason}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        <div className="px-6 pb-6 flex-shrink-0">
          <button
            onClick={() => setSummaryModalOpen(false)}
            className="w-full text-sm font-medium bg-gray-900 text-white rounded-xl py-2.5 hover:bg-gray-700 transition-colors"
          >
            Done
          </button>
        </div>
      </Modal>

      {/* Processing loader dialog */}
      <Modal
        isOpen={!!processingState}
        onClose={() => {}}
        containerClassName="bg-white rounded-2xl relative top-20 mx-auto w-full max-w-xs shadow-xl flex flex-col overflow-hidden pointer-events-none"
      >
        <div className="px-8 py-8 flex flex-col items-center gap-4 w-full">
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-800">
              {processingState?.mode === "upload" ? "Creating" : "Updating"} asset{processingState?.total !== 1 ? "s" : ""}…
            </p>
          </div>

          <div className="w-full">
            <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-200 ease-out"
                style={{
                  width: `${processingState?.total ? Math.min(100, (processingState.processed / processingState.total) * 100) : 0}%`,
                }}
              />
            </div>
            <div className="flex justify-between mt-1.5">
              <p className="text-xs text-gray-500">
                {processingState?.processed ?? 0} of {processingState?.total ?? 0} processed
              </p>
              <p className="text-xs font-medium text-gray-700">
                {processingState?.total ? Math.min(100, Math.round((processingState.processed / processingState.total) * 100)) : 0}%
              </p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-xs text-gray-500">Please don't close this page.</p>
          </div>
        </div>
      </Modal>

      {/* Interrupted dialog — offers to resume from where it stopped */}
      <Modal
        isOpen={!!failureState}
        onClose={handleCancelRetry}
        containerClassName="bg-white rounded-2xl relative top-20 mx-auto w-full max-w-sm shadow-xl flex flex-col overflow-hidden"
      >
        <div className="px-6 pt-10 pb-4 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-900">
            {failureState?.mode === "upload" ? "Upload" : "Update"} Interrupted
          </h2>
          <p className="text-sm text-gray-500 mt-1">{failureState?.message}</p>
        </div>

        <div className="px-6 pb-2 flex-shrink-0">
          <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-500 rounded-full"
              style={{
                width: `${failureState?.total ? Math.min(100, (failureState.processed / failureState.total) * 100) : 0}%`,
              }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1.5">
            {failureState?.processed ?? 0} of {failureState?.total ?? 0} completed before it stopped.
          </p>
        </div>

        <div className="px-6 pt-4 pb-6 flex-shrink-0 flex gap-3">
          <button
            onClick={handleCancelRetry}
            className="flex-1 text-sm font-medium bg-gray-100 text-gray-800 rounded-xl py-2.5 hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleRetry}
            className="flex-1 text-sm font-medium bg-gray-900 text-white rounded-xl py-2.5 hover:bg-gray-700 transition-colors"
          >
            Retry Remaining
          </button>
        </div>
      </Modal>

      <ActionBar>
        <input
          id="fileInput"
          type="file"
          accept=".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={handleFileUpload}
        />
        <Button loading={isUploading} handleClick={() => triggerFile("upload")}>
          Bulk Upload
        </Button>
        <Button loading={isEditing} handleClick={() => triggerFile("edit")}>
          Bulk Edit
        </Button>
        <a href="/sample_assets.xlsx" download>
          <Button>Download Sample</Button>
        </a>
        {children}
      </ActionBar>
    </>
  );
};

export default AssetActionBar;
